import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface LicenseEntry {
    packageName: string;
    licenseType: string;
    licenseText: string;
}

@Component({
    selector: 'app-third-party-licenses',
    templateUrl: './third-party-licenses.component.html',
    styleUrls: ['./third-party-licenses.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule, MatButtonModule, MatExpansionModule, MatIconModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThirdPartyLicensesComponent implements OnInit {
    private readonly http = inject(HttpClient);

    readonly licenses = signal<LicenseEntry[]>([]);
    readonly loading = signal(true);
    readonly error = signal(false);

    async ngOnInit(): Promise<void> {
        try {
            const content = await lastValueFrom(
                this.http.get('/3rdpartylicenses.txt', { responseType: 'text' }).pipe(
                    catchError(() => this.http.get('/assets/3rdpartylicenses.txt', { responseType: 'text' })),
                ),
            );
            this.licenses.set(this.parseLicenses(content));
        } catch {
            this.error.set(true);
        } finally {
            this.loading.set(false);
        }
    }

    private parseLicenses(content: string): LicenseEntry[] {
        const separator = /^-{40,}$/m;
        const blocks = content.split(separator).filter(block => block.trim().length > 0);

        return blocks.map(block => {
            const lines = block.trim().split('\n');
            let packageName = '';
            let licenseType = '';
            const textLines: string[] = [];

            for (const line of lines) {
                if (line.startsWith('Package:')) {
                    packageName = line.replace('Package:', '').trim();
                } else if (line.startsWith('License:')) {
                    licenseType = line.replace('License:', '').trim().replace(/"/g, '');
                } else {
                    textLines.push(line);
                }
            }

            return {
                packageName,
                licenseType,
                licenseText: textLines.join('\n').trim(),
            };
        }).filter(entry => entry.packageName.length > 0);
    }

    back(): void {
        window.history.back();
    }
}
